/** @jsxImportSource @emotion/react */
import React from 'react';
import css from '@emotion/css';
import debounceRender from 'react-debounce-render';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Fade from '@material-ui/core/Fade';
import createTree from '../../utils/createTree';
import styled from 'styled-components/macro';
import { Formik } from 'formik';

// import deepEqual from 'fast-deep-equal';

import { fieldTypes } from './fieldTypes';

import { CellMeasurerCache, List } from 'react-virtualized';

import { rgba } from 'polished';

import Markdown from '../../components/Markdown';

import FormHelperText from './FormHelperText';
import { getAllFields } from './getAllFields';
import { RequiredAsterisk } from '../Asterisk';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ReadMore from '../../components/ReadMore';

const getUITypeProps = () => {
  return uiType => {
    switch (uiType) {
      case 'long_text':
        return { multiline: true };
      case 'file_upload':
        return {
          submissionId: 'preview',
          formId: 'preview',
          getOrCreateSubmission: () => Promise.resolve('preview'),
          isPreview: true
        };
      default:
        return {};
    }
  };
};

export const getFieldById = (fields, id) =>
  fields.find(field => field.internalId === id);

const FieldWrapper = styled.div`
  padding: 16px 48px;
  margin: 0 -32px;
  background-color: ${({
    theme,
    hasError,
    readOnly,
    isAdminOnly,
    fieldReadOnly
  }) =>
    readOnly
      ? 'transparent'
      : hasError
      ? rgba(theme.palette.error.main, 0.1)
      : // TODO disabled background on focus -- check focus for multiple choice
      // : isFocused
      // ? rgba(theme.palette.primary.light, 0.1)
      isAdminOnly
      ? rgba(theme.palette.primary.main, 0.1)
      : 'transparent'};

  transition: background-color linear 0.2s;

  [class*='MuiInputBase-root'] {
    background: rgba(255, 255, 255, 0.6) !important;
    font-size: 12px !important;
  }

  [class*='MuiInputBase-input'] {
    padding: 10px 14px !important;
  }

  [class*='MuiOutlinedInput-inputMultiline'] {
    padding: 0 !important;
  }

  p {
    font-size: 14px;
  }

  p img {
    max-width: 100%;
  }
`;

const HalfFade = styled.div`
  opacity: ${({ show }) => (show ? 1 : 0.5)};
  background: ${({ show }) => (show ? 'transparent' : '#dedede')};
  transition: 0.2s;
  margin: 0 -16px;
`;

const InputWrapper = styled.div`
  padding: 16px 0;
  clear: both;
`;

const StyledLock = styled(LockOutlinedIcon)`
  font-size: 1.1rem !important;
  margin-right: 0.2rem;
  opacity: 0.7;
  vertical-align: top;
`;

const MDWrapper = styled.div`
  display: flex;
  p {
    ${({ noBold }) => !noBold && 'font-weight: 600;'}
    display: inline;
    ${({ fontSize }) => fontSize && 'font-size: ' + fontSize + 'px !important;'}
  }
`;

const EndUserField = ({
  hidden,
  isPreview = true,
  getUITypeProps,
  index,
  fieldCount,
  previousFieldType,
  title,
  description,
  isAdminOnly,
  options,
  required,
  uiType,
  internalId,
  readOnly: fieldReadOnly
}) => {
  const showTitle = !['profile_field', 'section_break'].includes(uiType);
  const [isFocused, setIsFocused] = React.useState(false);
  const value = null;

  return (
    <HalfFade show={!hidden} appear>
      <FieldWrapper
        isPreview={isPreview}
        isAdminOnly={isAdminOnly}
        fieldReadOnly={fieldReadOnly}
        isFocused={isFocused}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => setIsFocused(false)}
      >
        <div
          key={`fieldwrapper-${internalId}`}
          data-testid={`enduserform-field-${internalId}`}
          data-required={required ? 1 : 0}
          data-admin-only={isAdminOnly ? 1 : 0}
          data-fieldid={internalId}
          data-value={value || ''}
        >
          {(uiType === 'section_break' || uiType === 'instructional_text') &&
            previousFieldType !== 'instructional_text' &&
            title &&
            index > 0 && (
              <Divider
                css={css`
                  && {
                    margin: -16px -48px 48px;
                  }
                `}
              />
            )}

          {uiType === 'section_break' &&
            (title ? (
              title
                .trim()
                .split('\n')
                .map((line, i, arr) => (
                  <h2
                    key={i}
                    style={{ textAlign: 'left', fontWeight: 700, margin: 0 }}
                  >
                    <MDWrapper fontSize="20">
                      <div>
                        {i === arr.length - 1 && fieldReadOnly && (
                          <StyledLock />
                        )}
                      </div>
                      <div>
                        {/* <Markdown content={line} /> */}
                        <Markdown
                          content={line}
                          allowedTypes={[
                            'root',
                            'text',
                            'link',
                            'emphasis',
                            'listItem',
                            'list'
                          ]}
                        />

                        {/* required asterisk on last title element */}
                        {i === arr.length - 1 && required && (
                          <RequiredAsterisk />
                        )}
                      </div>
                    </MDWrapper>
                  </h2>
                ))
            ) : isPreview ? (
              <p style={{ opacity: 0.5, fontStyle: 'italic' }}>
                Please write a heading for this section.
              </p>
            ) : null)}

          {/* title, split into <p>'s */}
          {showTitle &&
            (title ? (
              <div data-testid={`field-title-${internalId}`}>
                {uiType === 'instructional_text' ? (
                  <ReadMore
                    needsMore={
                      title.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1').length >
                      400
                    }
                  >
                    {title
                      .trim()
                      .split('\n')
                      .map((line, i, arr) => (
                        <p
                          key={i}
                          style={{
                            textAlign: 'left',
                            fontWeight:
                              uiType === 'instructional_text' ? null : 700
                          }}
                        >
                          <MDWrapper noBold>
                            <div>
                              {i === arr.length - 1 && fieldReadOnly && (
                                <StyledLock />
                              )}
                            </div>
                            <div>
                              <Markdown content={line} />

                              {i === arr.length - 1 && required && (
                                <RequiredAsterisk />
                              )}
                            </div>
                          </MDWrapper>
                        </p>
                      ))}
                  </ReadMore>
                ) : (
                  <>
                    {title
                      .trim()
                      .split('\n')
                      .map((line, i, arr) => (
                        <p
                          key={i}
                          style={{
                            textAlign: 'left',
                            fontWeight:
                              uiType === 'instructional_text' ? null : 700
                          }}
                        >
                          <MDWrapper>
                            <div>
                              {i === arr.length - 1 && fieldReadOnly && (
                                <StyledLock />
                              )}
                            </div>
                            <div>
                              {/* <Markdown content={line} /> */}
                              <Markdown
                                content={line}
                                allowedTypes={[
                                  'root',
                                  'text',
                                  'link',
                                  'emphasis',
                                  'listItem',
                                  'list'
                                ]}
                              />

                              {i === arr.length - 1 && required && (
                                <RequiredAsterisk />
                              )}
                            </div>
                          </MDWrapper>
                        </p>
                      ))}
                  </>
                )}
              </div>
            ) : isPreview ? (
              <p style={{ opacity: 0.5, fontStyle: 'italic' }}>
                {uiType === 'instructional_text'
                  ? 'Please write instructional text.'
                  : 'Please write a question for this field.'}
              </p>
            ) : null)}

          {/* description, split into <p>'s */}
          {description && (
            <div
              data-testid={`field-description-${internalId}`}
              css={'margin: 12px 0;'}
            >
              <ReadMore
                needsMore={
                  description.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1').length >
                  400
                }
              >
                <Markdown content={description} />
              </ReadMore>
            </div>
          )}

          {isAdminOnly && (
            <FormHelperText style={{ margin: '24px 0 0' }}>
              This field is visible to admins only.
            </FormHelperText>
          )}
          {uiType !== 'section_break' && (
            <InputWrapper>
              {createTree({
                type: fieldTypes[uiType].component || 'Fields.Text',
                name: internalId,
                options: options,
                required: required && !hidden,
                disabled: hidden,
                readOnly: true,
                hidden,
                isPreview: true,
                wait: 150,
                ...getUITypeProps(uiType)
              })}
            </InputWrapper>
          )}
          {uiType === 'instructional_text' && index !== fieldCount - 1 && (
            <Divider
              css={css`
                && {
                  margin: 12px -48px 0;
                }
              `}
            />
          )}
        </div>
      </FieldWrapper>
    </HalfFade>
  );
};

const getEmptyInitialValues = schema => {
  let initialValues = {};

  // create empty initial values
  getAllFields(schema).forEach(f => {
    if (!['section_break'].includes(f.uiType)) {
      initialValues[f.internalId] = '';
    }
  });

  return initialValues;
};

const getRowRender = (allFields, measureCach, formId) => props => {
  const { index } = props;
  const field = allFields[index];

  let { internalId: id, options } = field;

  // const isHiddenField = hidden.includes(id);
  // previousFieldType = fieldType;
  // fieldType = uiType;
  options = options.filter(s => s.trim().length > 0);
  // if (uiType === 'section_break') {
  //   isInHiddenSection = isHiddenField;
  // }
  const optionsString = Array.isArray(options) ? options.join('\x01') : '';
  // const descriptionsRule = rules.find(
  //   rule => rule.action.actionType === 'description' && rule.fieldId === id
  // );

  return (
    <EndUserField
      key={id + optionsString}
      {...{
        // title,
        // uiType,
        // descriptionFromRules: descriptions[id],
        // descriptionsRule,
        // baseValues,
        // fields,
        // logicInfo: info[id],
        // isPreview,
        // hidden: isHiddenField || isInHiddenSection,
        // readOnly,
        getUITypeProps,
        // getFieldTitleById,
        index,
        // fieldCount,
        formId,
        // previousFieldType,
        ...field
      }}
    />
  );
};

function PreviewForm({
  formId,
  form = null,
  scrollToIndex,
  previewListRef,
  previewHeight
}) {
  const schema = form;
  const allFields = schema ? getAllFields(schema).filter(f => !f.archived) : [];
  const data = getEmptyInitialValues(schema);

  const submissionId = 'preview' + formId;

  const loaded = Boolean(schema && data);

  const { current: measureCache } = React.useRef(
    new CellMeasurerCache({
      defaultHeight: 100,
      minHeight: 75,
      fixedWidth: true
    })
  );

  return (
    <Formik
      validate={() => true}
      render={formik => {
        return (
          <div
            style={{ position: 'relative', pointerEvents: 'none' }}
            data-submissionid={submissionId}
          >
            <Collapse appear in={!!schema}>
              <div>
                {
                  <>
                    {!loaded && <CircularProgress />}
                    <Fade in={loaded}>
                      <div>
                        {schema && (
                          <List
                            rowCount={allFields.length}
                            height={previewHeight}
                            scrollToIndex={scrollToIndex}
                            ref={previewListRef}
                            deferredMeasurementCache={measureCache}
                            rowHeight={measureCache.rowHeight}
                            estimatedRowSize={200}
                            width={464}
                            overscanRowCount={10}
                            scrollToAlignment="center"
                            scroll
                            rowRenderer={getRowRender(
                              allFields,
                              measureCache,
                              formId
                            )}
                          />
                        )}
                      </div>
                    </Fade>
                  </>
                }
              </div>
            </Collapse>
          </div>
        );
      }}
    />
  );
}

export default debounceRender(PreviewForm, 50, {
  leading: false,
  trailing: true
});
